import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { CodeXml, Loader2 } from "lucide-react";

import { auth } from "../../firebase";
import { signInWithEmailAndPassword } from "firebase/auth";
import analyticsService from "../../services/analyticsService";

import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

import { cn } from "../../lib/utils";

import { isEmailValid } from "../../helpers/emailHelper";
import { useToast } from "../../components/hooks/use-toast";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isValid, setIsValid] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    if (isEmailValid(email) && password.length > 0) {
      setIsValid(true);
    } else {
      setIsValid(false);
    }
  }, [email, password]);

  const login = async () => {
    setIsLoggingIn(true);

    try {
      await signInWithEmailAndPassword(auth, email, password);
      
      // Track successful login event
      await analyticsService.trackLogin('email');

      navigate("/");

      toast({
        title: "Log in",
        description: "Log in successful.",
      });
    } catch (error) {
      console.error("Log in error:", error);
      
      // Track login error
      await analyticsService.trackError('auth_error', error.message, 'login');

      toast({
        variant: "destructive",
        title: "Uh oh! Something went wrong.",
        description: "There was a error logging you in.",
      });
    } finally {
      setIsLoggingIn(false);
    }
  };

  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-6 bg-muted p-6 md:p-10">
      <div className="flex w-full max-w-sm flex-col gap-6">
        <a href="/" className="flex items-center gap-2 self-center font-medium">
          <div className="flex h-6 w-6 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <CodeXml className="size-4" />
          </div>
          Allayze
        </a>

        <div className="flex flex-col gap-6">
          <Card>
            <CardHeader className="text-center">
              <CardTitle className="text-xl">Welcome back</CardTitle>

              <CardDescription>
                Log in with your account
              </CardDescription>
            </CardHeader>

            <CardContent>
              <div className="grid gap-6">
                <div className="grid gap-6">
                  <div className="grid gap-2">
                    <Label htmlFor="email">Email</Label>

                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="abc@example.com"
                      disabled={isLoggingIn}
                      required
                    />
                  </div>

                  <div className="grid gap-2">
                    <div className="flex items-center">
                      <Label htmlFor="password">Password</Label>

                      <Link to="/reset-password" className="ml-auto">
                        <span
                          className={cn(
                            "text-sm underline-offset-4 cursor-pointer",
                            isLoggingIn
                              ? "cursor-none text-muted-foreground pointer-events-none"
                              : "hover:underline"
                          )}
                          disabled={isLoggingIn}
                        >
                          Forgot your password?
                        </span>
                      </Link>
                    </div>

                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={isLoggingIn}
                      required
                    />
                  </div>

                  <Button
                    className="w-full"
                    disabled={!isValid || isLoggingIn}
                    onClick={login}
                  >
                    {isLoggingIn ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        <span>Logging in...</span>
                      </>
                    ) : (
                      <>
                        <span>Log in</span>
                      </>
                    )}
                  </Button>
                </div>

                <div
                  className={cn(
                    "text-center text-sm",
                    isLoggingIn
                      ? "cursor-none text-muted-foreground pointer-events-none"
                      : ""
                  )}
                >
                  Don&apos;t have an account?{" "}
                  <Link to="/signup">
                    <span
                      disabled={isLoggingIn}
                      className="underline underline-offset-4"
                    >
                      Sign up
                    </span>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="text-balance text-center text-xs text-muted-foreground [&_a]:underline [&_a]:underline-offset-4 [&_a]:hover:text-primary  ">
            By logging in, you agree to our <span>Terms of Service</span> and{" "}
            <span>Privacy Policy</span>.
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
